import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import { AlertOctagon, AlertTriangle, CheckCircle2, Info, Pause, Play } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { isWallDisplayOnly } from "../../auth/roles";
import { consumeBootstrappedTicker } from "../../api/bootstrapApi";
import { fetchTicker, type TickerDto, type TickerItemDto, type TickerSeverity, type TickerSpeed } from "../../api/tickerApi";

export const TICKER_PREFS_KEY = "summitops.ticker.prefs";
export const DEFAULT_POLL_SECONDS = 60;
export const REDUCED_MOTION_INTERVAL_MS = 8_000;

export interface TickerPrefs {
  enabled: boolean;
  speed: TickerSpeed | null;
  /** "scroll" (default, owner 2026-10-21: "This should be a scrolling ticker") or "step" (one item at a time). */
  motion: "scroll" | "step";
}

const DEFAULT_PREFS: TickerPrefs = {
  enabled: true,
  speed: null,
  motion: "scroll"
};

export function readTickerPrefs(): TickerPrefs {
  try {
    const raw = window.localStorage.getItem(TICKER_PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<TickerPrefs>;
    const enabled = typeof parsed.enabled === "boolean" ? parsed.enabled : true;
    const speed = parsed.speed === "slow" || parsed.speed === "medium" || parsed.speed === "fast" ? parsed.speed : null;
    const motion = parsed.motion === "step" ? "step" : "scroll";
    return { enabled, speed, motion };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function writeTickerPrefs(prefs: TickerPrefs): void {
  try {
    window.localStorage.setItem(TICKER_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Ignore storage quota errors in restricted browsing contexts.
  }
}

function detectReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return Boolean(window.matchMedia("(prefers-reduced-motion: reduce)")?.matches);
}

function secondsPerItem(speed: TickerSpeed): number {
  switch (speed) {
    case "slow":
      return 10;
    case "fast":
      return 3.5;
    case "medium":
    default:
      return 6;
  }
}

function SeverityIcon({ severity, className }: { severity: TickerSeverity; className?: string }) {
  switch (severity) {
    case "critical":
      return <AlertOctagon aria-hidden="true" className={className} />;
    case "attention":
      return <AlertTriangle aria-hidden="true" className={className} />;
    case "good-news":
      return <CheckCircle2 aria-hidden="true" className={className} />;
    case "info":
    default:
      return <Info aria-hidden="true" className={className} />;
  }
}

function severityBadgeClasses(severity: TickerSeverity, isWall: boolean): string {
  if (isWall) {
    switch (severity) {
      case "critical":
        return "border-red-400/60 bg-red-950/90 text-red-200";
      case "attention":
        return "border-amber-400/60 bg-amber-950/90 text-amber-200";
      case "good-news":
        return "border-emerald-400/60 bg-emerald-950/90 text-emerald-200";
      case "info":
      default:
        return "border-sky-400/60 bg-sky-950/90 text-sky-200";
    }
  }

  switch (severity) {
    case "critical":
      return "border-red-200 bg-red-50 text-red-700";
    case "attention":
      return "border-amber-200 bg-amber-50 text-amber-800";
    case "good-news":
      return "border-emerald-200 bg-emerald-50 text-emerald-800";
    case "info":
    default:
      return "border-slate-200 bg-slate-100 text-slate-700";
  }
}

export interface TickerProps {
  /** "header" (default) fits the h-12 AppShell bar; "wall" uses text-2xl high-contrast navy styling. */
  variant?: "header" | "wall";
  /** Poll interval in seconds (defaults to 60 s). */
  pollSeconds?: number;
}

/**
 * Header and wall-display plant signal ticker (D-O61 W2, D-O64, D-O67, O-R38, Q-O66).
 * Role-aware, accessible (severity icon + word, never colour-only, `aria-live="off"`, keyboard arrow stepping,
 * respects `prefers-reduced-motion`), and caches via ETag 304.
 */
export function Ticker({ variant = "header", pollSeconds = DEFAULT_POLL_SECONDS }: TickerProps) {
  const { principal } = useAuth();
  const isWall = variant === "wall";
  const roles = principal?.roles ?? [];

  // Hidden when signed out, when principal has no roles, and for the WallDisplay persona in the header.
  const shouldHideForAuth =
    !principal ||
    roles.length === 0 ||
    (!isWall && isWallDisplayOnly(roles));

  const scopeKey = useMemo(
    () => (principal ? `${principal.userPrincipalName ?? "anon"}:${roles.join(",")}` : "signed-out"),
    [principal, roles]
  );

  const [data, setData] = useState<TickerDto | null>(null);
  const [prefs] = useState<TickerPrefs>(() => readTickerPrefs());
  const [manualPaused, setManualPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focusedWithin, setFocusedWithin] = useState(false);
  const [steppedMode, setSteppedMode] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [reducedMotion, setReducedMotion] = useState<boolean>(() => detectReducedMotion());

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    setReducedMotion(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const loadTicker = useCallback(async () => {
    if (shouldHideForAuth) return;
    const bootstrapped = consumeBootstrappedTicker(scopeKey);
    if (bootstrapped) {
      setData(bootstrapped);
      return;
    }
    try {
      const next = await fetchTicker({ scopeKey });
      if (next) {
        setData(next);
      }
    } catch {
      // Keep last known items on transient network failure.
    }
  }, [shouldHideForAuth, scopeKey]);

  useEffect(() => {
    if (shouldHideForAuth) {
      setData(null);
      return;
    }
    void loadTicker();
    const intervalMs = Math.max(5, pollSeconds) * 1000;
    const timer = window.setInterval(() => {
      void loadTicker();
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [shouldHideForAuth, loadTicker, pollSeconds]);

  const items: TickerItemDto[] = data?.items ?? [];

  useEffect(() => {
    if (items.length > 0 && activeIndex >= items.length) {
      setActiveIndex(0);
    }
  }, [items.length, activeIndex]);

  const isPaused = manualPaused || hovered || focusedWithin;

  // The header ticker always scrolls at medium speed and pauses only while hovered or focused (owner, 2026-10-22: no
  // Pause or Show all buttons). Stored speed / step / hide choices apply to the wall board only.
  const stepMode = isWall && prefs.motion === "step";
  useEffect(() => {
    if (!stepMode || isPaused || items.length <= 1) return;
    const timer = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % items.length);
    }, REDUCED_MOTION_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [stepMode, isPaused, items.length]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (items.length === 0) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        setSteppedMode(true);
        setActiveIndex((prev) => (prev + 1) % items.length);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        setSteppedMode(true);
        setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
      }
    },
    [items.length]
  );

  // Render nothing when signed out, when WallDisplay in header, or when there are no items.
  if (shouldHideForAuth || items.length === 0) {
    return null;
  }

  const effectiveSpeed: TickerSpeed = isWall ? prefs.speed ?? data?.speedDefault ?? "medium" : "medium";
  const durationSeconds = Math.max(12, Math.round(items.length * secondsPerItem(effectiveSpeed)));
  const currentItem = items[activeIndex] ?? items[0]!;
  const useSingleItemView = stepMode || steppedMode;

  return (
    <div
      ref={containerRef}
      role="region"
      aria-label="Plant operations ticker"
      aria-live="off"
      tabIndex={0}
      data-testid={isWall ? "ticker-wall" : "ticker-bar"}
      data-paused={isPaused ? "true" : "false"}
      data-reduced-motion={reducedMotion ? "true" : "false"}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocusedWithin(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setFocusedWithin(false);
          setSteppedMode(false);
        }
      }}
      onKeyDown={handleKeyDown}
      className={
        isWall
          ? "relative flex min-w-0 items-center gap-3 rounded-lg border border-brand-900 bg-brand-900 px-4 py-2.5 text-2xl text-white shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-viper-cyan"
          : "relative flex h-9 min-w-0 max-w-full items-center gap-1.5 text-sm text-slate-800 outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-1"
      }
    >
      <style>{`
        @keyframes summitops-ticker-scroll {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }
        /* The ticker always scrolls (owner 2026-10-21 / 2026-10-22), even when the device or the person reduces motion:
           html.reduce-motion collapses every other animation, so restore this one. Hover or focus still pauses it. */
        html.reduce-motion .summitops-ticker-track {
          animation-duration: var(--summitops-ticker-duration) !important;
          animation-iteration-count: infinite !important;
        }
      `}</style>

      {/* Main signal viewport with subtle fade edges and truncation */}
      <div
        className="relative flex min-w-0 flex-1 items-center overflow-hidden"
        data-testid="ticker-viewport"
      >
        {useSingleItemView ? (
          <div
            key={currentItem.signalKey}
            className="flex min-w-0 flex-1 items-center transition-opacity duration-300"
            data-testid="ticker-single-item"
            data-active-signal-key={currentItem.signalKey}
          >
            <TickerSignalLink item={currentItem} isWall={isWall} truncate />
          </div>
        ) : (
          <div
            className="summitops-ticker-track flex w-max shrink-0 items-center gap-10 whitespace-nowrap pr-10 max-md:w-full max-md:!animate-none"
            data-testid="ticker-track"
            data-speed={effectiveSpeed}
            style={{
              ["--summitops-ticker-duration" as string]: `${durationSeconds}s`,
              animationName: "summitops-ticker-scroll",
              animationDuration: `${durationSeconds}s`,
              animationTimingFunction: "linear",
              animationIterationCount: "infinite",
              animationPlayState: isPaused ? "paused" : "running"
            }}
          >
            {items.map((item, idx) => (
              <div
                key={item.signalKey}
                // Every item keeps its full width (shrink-0) so the strip is as long as its content and truly scrolls.
                // On phones only the first item shows, truncated.
                className={idx === 0 ? "min-w-0 max-md:flex-1 max-md:truncate md:shrink-0" : "hidden md:inline-flex md:shrink-0"}
                data-testid="ticker-item"
                data-signal-key={item.signalKey}
              >
                <TickerSignalLink item={item} isWall={isWall} truncate={false} />
              </div>
            ))}
            {/* A second copy makes the loop seamless (the strip slides by exactly half its length). Hidden from assistive
                technology and keyboard focus so every signal is announced and reachable once. */}
            {items.map((item) => (
              <div
                key={`${item.signalKey}-loop`}
                className="hidden md:inline-flex md:shrink-0"
                aria-hidden="true"
                data-testid="ticker-item-loop"
                {...({ inert: "" } as Record<string, string>)}
              >
                <TickerSignalLink item={item} isWall={isWall} truncate={false} />
              </div>
            ))}
          </div>
        )}

        {!isWall && !useSingleItemView && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 hidden w-6 bg-gradient-to-l from-white/90 to-transparent md:block"
          />
        )}
      </div>

      {/* Controls: the wall board keeps its Pause button; the header ticker has none (owner, 2026-10-22). */}
      {isWall && (
        <div className="flex flex-none items-center gap-1">
          <button
            type="button"
            onClick={() => setManualPaused((prev) => !prev)}
            aria-label={manualPaused ? "Resume ticker" : "Pause ticker"}
            aria-pressed={manualPaused}
            data-testid="ticker-pause-button"
            className="inline-flex items-center gap-1.5 rounded-md border border-white/20 bg-white/10 px-2.5 py-1 text-sm font-medium text-white hover:bg-white/20"
          >
            {manualPaused ? <Play aria-hidden="true" className="h-4 w-4" /> : <Pause aria-hidden="true" className="h-4 w-4" />}
            <span>{manualPaused ? "Resume" : "Pause"}</span>
          </button>
        </div>
      )}
    </div>
  );
}

function TickerSignalLink({
  item,
  isWall,
  truncate
}: {
  item: TickerItemDto;
  isWall: boolean;
  truncate?: boolean;
}) {
  return (
    <Link
      to={item.linkPath}
      className={`inline-flex min-w-0 items-center gap-1.5 rounded px-1 py-0.5 transition-colors ${
        isWall ? "text-white hover:bg-white/10" : "text-slate-800 hover:bg-slate-100"
      } ${truncate ? "max-md:max-w-full max-md:truncate" : ""}`}
    >
      <span
        className={`inline-flex flex-none items-center gap-1 rounded border px-1.5 py-0.5 font-semibold ${
          isWall ? "text-base" : "text-[11px]"
        } ${severityBadgeClasses(item.severity, isWall)}`}
      >
        <SeverityIcon severity={item.severity} className={isWall ? "h-5 w-5" : "h-3 w-3"} />
        <span>{item.severityLabel}</span>
      </span>
      <span className={truncate ? "truncate" : ""}>{item.text}</span>
    </Link>
  );
}
